/* eslint-disable @typescript-eslint/no-explicit-any */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';

import { IResumoFluxoConta, IResumoFluxoForma } from '../../../shared/services/api/Financeiro/Caixa';

(pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;

const moeda = (valor: number) => (valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TIPO_CONTA: Record<string, string> = {
  banco: 'Banco', caixa: 'Caixa', cofre: 'Cofre', recebiveis: 'Recebíveis',
};

interface IImpressaoFluxoContas {
  datainicial: string; // YYYY-MM-DD
  datafinal: string;
  formas: IResumoFluxoForma[];
  contas: IResumoFluxoConta[];
  transferencias: number;
  // Lançamento por lançamento em cada forma (formas[].detalhe).
  detalhado?: boolean;
}

const NATUREZA: Record<string, string> = { transferencia: ' (transf. p/ banco)', taxa: ' (taxa)', movimento: '' };

// Uma tabela por forma de pagamento com cada lançamento dela.
const secaoDetalhada = (formas: IResumoFluxoForma[]) => {
  const blocos: any[] = [{ text: 'Lançamentos por forma de pagamento', style: 'secao', margin: [0, 18, 0, 6], pageBreak: 'before' }];
  formas.filter((f) => (f.detalhe || []).length > 0).forEach((f) => {
    const linhas = f.detalhe || [];
    blocos.push({
      table: {
        headerRows: 2,
        dontBreakRows: true,
        widths: [52, 70, 42, '*', 58, 58],
        body: [
          [
            { text: f.forma, colSpan: 4, bold: true, fontSize: 9.5, fillColor: '#E5E7EB' }, {}, {}, {},
            { text: moeda(f.entrada), alignment: 'right', bold: true, fontSize: 8.5, color: '#047857', fillColor: '#E5E7EB' },
            { text: moeda(f.saida), alignment: 'right', bold: true, fontSize: 8.5, color: '#B91C1C', fillColor: '#E5E7EB' },
          ],
          [
            { text: 'Data', style: 'cab' }, { text: 'Conta', style: 'cab' }, { text: 'Nº', style: 'cab' },
            { text: 'Parceiro / histórico', style: 'cab' }, { text: 'Entrada', style: 'cab', alignment: 'right' }, { text: 'Saída', style: 'cab', alignment: 'right' },
          ],
          ...linhas.map((l) => [
            { text: `${dayjs(l.datalan).format('DD/MM/YY')}${l.hora ? `\n${l.hora}` : ''}`, fontSize: 7.5 },
            { text: l.conta, fontSize: 7.5 },
            { text: l.numero || '', fontSize: 7.5 },
            {
              text: [
                ...(l.parceiro ? [{ text: `${l.parceiro}\n`, bold: true }] : []),
                { text: `${l.historico}${NATUREZA[l.natureza] || ''}`, color: '#4B5563' },
              ],
              fontSize: 7.5,
            },
            { text: l.entrada ? moeda(l.entrada) : '', alignment: 'right', fontSize: 7.5, color: '#047857' },
            { text: l.saida ? moeda(l.saida) : '', alignment: 'right', fontSize: 7.5, color: '#B91C1C' },
          ]),
        ],
      },
      layout: { hLineColor: () => '#D1D5DB', vLineColor: () => '#D1D5DB' },
      margin: [0, 0, 0, 10],
    });
  });
  return blocos;
};

// PDF do relatório do fluxo de todas as contas (ver DialogRelatorioFluxo):
// totais recebidos e pagos por forma de pagamento e por conta.
export const imprimirFluxoContas = ({ datainicial, datafinal, formas, contas, transferencias, detalhado }: IImpressaoFluxoContas) => {
  const totalEntrada = formas.reduce((s, f) => s + f.entrada, 0);
  // Saída da forma inclui o transferido pro banco; nos quadros de total ele
  // fica à parte (não é dinheiro saindo da empresa).
  const totalSaidaFormas = formas.reduce((s, f) => s + f.saida, 0);
  const totalTransferido = formas.reduce((s, f) => s + f.transferido, 0);
  const totalSaida = totalSaidaFormas - totalTransferido;
  const periodo = datainicial === datafinal
    ? dayjs(datainicial).format('DD/MM/YYYY')
    : `${dayjs(datainicial).format('DD/MM/YYYY')} a ${dayjs(datafinal).format('DD/MM/YYYY')}`;

  const cab = (texto: string, alignment = 'left') => ({ text: texto, style: 'cab', alignment });
  const cel = (texto: string, alignment = 'left', extra: any = {}) => ({ text: texto, style: 'cel', alignment, ...extra });

  const tabelaFormas = {
    table: {
      headerRows: 1,
      widths: ['*', 90, 90, 90, 45],
      body: [
        [cab('Forma de pagamento'), cab('Recebido', 'right'), cab('Saída', 'right'), cab('Saldo', 'right'), cab('Lanç.', 'center')],
        ...formas.map((f) => [
          cel(f.forma),
          cel(moeda(f.entrada), 'right', { color: '#047857' }),
          {
            text: [
              { text: moeda(f.saida), color: '#B91C1C' },
              f.transferido > 0 || f.taxa > 0
                ? { text: `\n${[f.transferido > 0 ? `transf. ${moeda(f.transferido)}` : '', f.taxa > 0 ? `taxa ${moeda(f.taxa)}` : ''].filter(Boolean).join(' · ')}`, fontSize: 7, color: '#6B7280' }
                : '',
            ],
            style: 'cel', alignment: 'right',
          },
          cel(moeda(f.entrada - f.saida), 'right', { bold: true }),
          cel(String(f.lancamentos), 'center'),
        ]),
        [
          cel('TOTAL', 'left', { bold: true }),
          cel(moeda(totalEntrada), 'right', { bold: true, color: '#047857' }),
          cel(moeda(totalSaidaFormas), 'right', { bold: true, color: '#B91C1C' }),
          cel(moeda(totalEntrada - totalSaidaFormas), 'right', { bold: true }),
          cel(String(formas.reduce((s, f) => s + f.lancamentos, 0)), 'center', { bold: true }),
        ],
      ],
    },
    layout: {
      fillColor: (linha: number, node: any) => (linha === 0 ? '#E5E7EB' : linha === node.table.body.length - 1 ? '#F3F4F6' : null),
      hLineColor: () => '#D1D5DB',
      vLineColor: () => '#D1D5DB',
    },
  };

  const tabelaContas = {
    table: {
      headerRows: 1,
      widths: ['*', 60, 85, 85, 80],
      // Transferência = saída na conta de onde sai e entrada na que recebe.
      body: [
        [cab('Conta'), cab('Tipo'), cab('Entrada', 'right'), cab('Saída', 'right'), cab('Saldo', 'right')],
        ...contas.map((c) => {
          const entrada = c.entrada + c.transferenciaEntrada;
          const saida = c.saida + c.transferenciaSaida;
          const detalhe = (valor: number) => (valor > 0 ? { text: `\ntransf. ${moeda(valor)}`, fontSize: 7, color: '#6B7280' } : '');
          return [
            cel(c.titular),
            cel(TIPO_CONTA[c.tipo] || c.tipo),
            { text: [{ text: moeda(entrada), color: '#047857' }, detalhe(c.transferenciaEntrada)], style: 'cel', alignment: 'right' },
            { text: [{ text: moeda(saida), color: '#B91C1C' }, detalhe(c.transferenciaSaida)], style: 'cel', alignment: 'right' },
            cel(moeda(entrada - saida), 'right', { bold: true }),
          ];
        }),
      ],
    },
    layout: {
      fillColor: (linha: number) => (linha === 0 ? '#E5E7EB' : null),
      hLineColor: () => '#D1D5DB',
      vLineColor: () => '#D1D5DB',
    },
  };

  const quadro = (titulo: string, valor: number, cor: string) => ({
    stack: [
      { text: titulo, fontSize: 8, color: '#6B7280' },
      { text: `R$ ${moeda(valor)}`, fontSize: 13, bold: true, color: cor },
    ],
    margin: [0, 0, 0, 0],
  });

  const documento: any = {
    pageSize: 'A4',
    pageMargins: [36, 40, 36, 40],
    content: [
      { text: detalhado ? 'Relatório do Fluxo das Contas — Detalhado' : 'Relatório do Fluxo das Contas', style: 'titulo' },
      { text: `Período: ${periodo}   ·   Emitido em ${dayjs().format('DD/MM/YYYY HH:mm')}`, fontSize: 9, color: '#4B5563', margin: [0, 2, 0, 12] },
      {
        columns: [
          quadro('Total recebido', totalEntrada, '#047857'),
          quadro('Total de saídas', totalSaida, '#B91C1C'),
          quadro('Transferido p/ banco', totalTransferido, '#1D4ED8'),
          quadro('Saldo do período', totalEntrada - totalSaida, totalEntrada - totalSaida >= 0 ? '#111827' : '#B91C1C'),
        ],
        columnGap: 12,
        margin: [0, 0, 0, 14],
      },
      { text: 'Por forma de pagamento', style: 'secao' },
      tabelaFormas,
      { text: 'Por conta', style: 'secao', margin: [0, 16, 0, 6] },
      tabelaContas,
      {
        text: `Transferências entre contas no período: R$ ${moeda(transferencias)} (recebíveis → banco, caixa → cofre, `
          + 'lançamento entre contas, abertura/fechamento de sessão). Na tabela por conta aparecem como saída de uma conta e '
          + 'entrada na outra; nos totais e por forma de pagamento não são somadas de novo, porque o valor já entrou quando foi '
          + 'recebido. Taxas de maquininha/boleto contam como saída.',
        fontSize: 8, color: '#6B7280', margin: [0, 10, 0, 0],
      },
      ...(detalhado ? secaoDetalhada(formas) : []),
    ],
    footer: (pagina: number, total: number) => ({ text: `Página ${pagina} de ${total}`, alignment: 'right', fontSize: 8, color: '#9CA3AF', margin: [0, 10, 36, 0] }),
    styles: {
      titulo: { fontSize: 16, bold: true },
      secao: { fontSize: 11, bold: true, margin: [0, 0, 0, 6] },
      cab: { fontSize: 8.5, bold: true },
      cel: { fontSize: 8.5 },
    },
    defaultStyle: { fontSize: 9 },
  };

  pdfMake.createPdf(documento).open();
};
